import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, Eye, EyeOff, Check, ShieldCheck, Sparkles } from 'lucide-react';
import { CinematicBackground } from '@/components/CinematicBackground';

// Mapa de la vista de entrada -> ruta de destino tras iniciar sesión.
const ENTRY_ROUTES = { calendar_hero: '/plannerapp/calendar', financial_hub: '/plannerapp/finance', project_board: '/plannerapp/tasks' };

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success('Bienvenido de nuevo');
      if (u.role === 'super_master') navigate('/plannerapp/admin');
      else if (u.role === 'assistant') navigate('/select-account');
      else navigate(ENTRY_ROUTES[u.entry_view] || '/plannerapp');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al iniciar sesión');
    }
    setBusy(false);
  };

  const perks = ['Calendario, tareas y finanzas unificadas', 'Notas con IA y captura de tickets', 'Bóveda cifrada y roles con permisos'];

  return (
    <div className="dark relative min-h-screen text-white overflow-x-hidden flex flex-col" style={{ background: '#07090A' }}>
      <CinematicBackground />
      <div className="relative z-10 flex-1 grid lg:grid-cols-2">
        {/* Panel de marca (desktop) */}
        <div className="hidden lg:flex flex-col justify-between p-12 border-r border-[rgba(168,230,206,0.1)]">
          <RadiantLogo size={56} />
          <div>
            <motion.h2 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-4xl font-black leading-tight pp-text-glow">
              El cockpit ejecutivo<br />del futuro
            </motion.h2>
            <p className="text-[#B7D2C6] mt-4 max-w-md">Toda tu operación en una sola experiencia cinematográfica, segura y con IA.</p>
            <div className="mt-8 space-y-3">
              {perks.map((p) => (
                <div key={p} className="flex items-center gap-3 text-sm text-[#e8f4ee]">
                  <span className="w-6 h-6 rounded-lg grid place-items-center shrink-0" style={{ background: 'rgba(62,180,137,0.18)', boxShadow: '0 0 16px rgba(62,180,137,0.25)' }}><Check size={14} className="pp-mint" /></span>{p}
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-[#7fa08f]"><ShieldCheck size={14} className="pp-mint" /> www.plannerpro.win · cifrado de extremo a extremo</div>
        </div>

        {/* Panel de formulario */}
        <div className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-[#B7D2C6] mb-6 hover:text-[#3EB489] transition-colors"><ArrowLeft size={16} /> Volver al inicio</button>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="relative rounded-[24px] p-8 sm:p-9"
              style={{
                background: 'linear-gradient(160deg, rgba(18,26,23,0.92), rgba(9,13,12,0.94))',
                border: '1px solid rgba(62,180,137,0.28)',
                boxShadow: '0 0 0 1px rgba(168,230,206,0.06), 0 30px 90px rgba(0,0,0,0.6), 0 0 60px rgba(62,180,137,0.10)',
                backdropFilter: 'blur(16px)',
              }}>
              {/* Logo imponente y radiante (siempre visible, protagonista en móvil/PWA) */}
              <div className="mb-7 flex flex-col items-center text-center">
                <RadiantLogo size={82} halo />
                <div className="mt-4 text-2xl font-extrabold tracking-tight">Planner<span className="pp-gold">PRO</span></div>
              </div>

              <h1 className="text-xl font-bold mb-1 text-center">Inicia sesión</h1>
              <p className="text-sm text-[#B7D2C6] mb-6 text-center">Accede a tu cockpit ejecutivo</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="relative">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#3EB489]" />
                  <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="tu@correo.com" autoComplete="email"
                    className="w-full h-12 pl-11 pr-3 rounded-xl bg-[rgba(255,255,255,0.06)] border border-[rgba(168,230,206,0.18)] text-white placeholder:text-[#6f8a7d] outline-none focus:border-[#3EB489] focus:ring-2 focus:ring-[rgba(62,180,137,0.35)] transition-[border-color,box-shadow]" />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#3EB489]" />
                  <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type={show ? 'text' : 'password'} placeholder="Contraseña" autoComplete="current-password"
                    className="w-full h-12 pl-11 pr-11 rounded-xl bg-[rgba(255,255,255,0.06)] border border-[rgba(168,230,206,0.18)] text-white placeholder:text-[#6f8a7d] outline-none focus:border-[#3EB489] focus:ring-2 focus:ring-[rgba(62,180,137,0.35)] transition-[border-color,box-shadow]" />
                  <button type="button" data-testid="toggle-password" onClick={() => setShow((s) => !s)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#B7D2C6] hover:text-[#3EB489] transition-colors">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
                <button data-testid="login-submit" disabled={busy}
                  className="w-full h-12 rounded-xl font-bold text-[#07090A] transition-transform active:scale-[0.98] disabled:opacity-70"
                  style={{ background: 'linear-gradient(135deg, #3EB489, #2f9d76)', boxShadow: '0 10px 30px rgba(62,180,137,0.35)' }}>
                  {busy ? 'Entrando...' : 'Entrar'}
                </button>
              </form>
              <button data-testid="google-login" onClick={() => toast.info('Google se activa al cargar las credenciales del proveedor.')}
                className="w-full h-12 mt-3 rounded-xl border border-[rgba(168,230,206,0.18)] text-white flex items-center justify-center gap-2 hover:bg-[rgba(255,255,255,0.05)] transition-colors">
                <img src="https://www.google.com/favicon.ico" alt="g" className="w-4 h-4" /> Continuar con Google
              </button>
              <p className="text-sm text-[#B7D2C6] mt-6 text-center">¿No tienes cuenta? <button onClick={() => navigate('/register')} className="pp-mint font-semibold hover:underline">Regístrate</button></p>
            </motion.div>

            <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-[#7fa08f]">
              <Sparkles size={12} className="pp-gold" /> Experiencia cinematográfica · segura · con IA
            </div>
          </div>
        </div>
      </div>

      {/* Footer branding (incluye login) */}
      <footer className="relative z-10 py-5 px-6 text-center border-t border-[rgba(168,230,206,0.1)]">
        <span className="text-xs text-[#7fa08f]">© 2026 PlannerPRO · www.plannerpro.win — Desarrollado por <a href="https://www.7-study.com" target="_blank" rel="noreferrer" className="pp-mint font-semibold hover:underline">7-study</a> (www.7-study.com) · Derechos reservados</span>
      </footer>
    </div>
  );
}

// Logo con halo radiante y pulso de brillo — protagonista del acceso.
const RadiantLogo = ({ size = 64, halo = false }) => (
  <div className="relative grid place-items-center" style={{ width: size, height: size }}>
    {halo && (
      <motion.div aria-hidden className="absolute rounded-full"
        style={{ width: size * 1.9, height: size * 1.9, background: 'radial-gradient(circle, rgba(62,180,137,0.32), transparent 70%)' }}
        animate={{ opacity: [0.5, 0.9, 0.5], scale: [0.92, 1.05, 0.92] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
    )}
    <motion.div
      className="relative rounded-2xl overflow-hidden bg-white"
      style={{ width: size, height: size, border: '1px solid rgba(201,168,76,0.5)' }}
      animate={halo ? { filter: ['drop-shadow(0 0 8px rgba(62,180,137,.45))', 'drop-shadow(0 0 26px rgba(62,180,137,.8))', 'drop-shadow(0 0 8px rgba(62,180,137,.45))'] } : {}}
      transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}>
      <img src="/brand/logo-principal.jpg" alt="PlannerPRO" className="w-full h-full object-cover" />
    </motion.div>
  </div>
);
